<template>
  <div :style="pageStyle">
    <div class="container py-4" style="max-width:560px;">
      <button class="btn btn-link p-0 mb-3" @click="$router.back()" :style="linkStyle">
        <i class="mdi mdi-arrow-left me-1"></i>Back
      </button>

      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-warning"></div>
        <div class="mt-3" :style="mutedStyle">Loading your transport request…</div>
      </div>

      <div v-else-if="error" class="card p-4" :style="cardStyle">
        <i class="mdi mdi-alert-circle" style="font-size:48px;color:#FF6B6B;"></i>
        <h5 class="mt-3">Couldn't load this transport request</h5>
        <p :style="mutedStyle">{{ error }}</p>
      </div>

      <div v-else>
        <div class="card p-4 mb-3" :style="cardStyle">
          <div style="font-size:11px;color:#BFA164;text-transform:uppercase;letter-spacing:1px;font-weight:700;">
            <i class="mdi mdi-truck me-1"></i>Transport Payment
          </div>
          <h4 class="mt-2 mb-1">Pay for trip {{ booking.booking_ref }}</h4>
          <div :style="mutedStyle" style="font-size:13px;">{{ booking.animal_count || 1 }} × {{ booking.animal_type_description || 'animal' }}</div>

          <!-- Total + progress -->
          <div class="my-3 text-center">
            <div :style="mutedStyle" style="font-size:11px;text-transform:uppercase;letter-spacing:1px;">Total due</div>
            <div style="font-size:34px;font-weight:700;color:#BFA164;line-height:1;">KES {{ formatPrice(totalDue) }}</div>
            <div v-if="paidSoFar > 0" :style="mutedStyle" style="font-size:12px;margin-top:6px;">
              Paid so far: <strong style="color:#2E7D32;">KES {{ formatPrice(paidSoFar) }}</strong> · Outstanding: <strong>KES {{ formatPrice(outstanding) }}</strong>
            </div>
          </div>

          <!-- Progress bar (only shown when split / partial) -->
          <div v-if="totalDue > 0 && (paidSoFar > 0 || splitTotal > 1)" class="mb-3">
            <div style="background:#E8E2DA;border-radius:10px;overflow:hidden;height:10px;">
              <div :style="{width: paidPercent + '%', background:'#2E7D32', height:'100%', transition:'width .3s'}"></div>
            </div>
            <div class="d-flex justify-content-between mt-1" :style="mutedStyle" style="font-size:11px;">
              <span>{{ paidPercent }}% paid</span>
              <span v-if="splitTotal > 1">{{ chunksDone }} of {{ splitTotal }} payments complete</span>
            </div>
          </div>

          <!-- Trip details -->
          <div class="mb-3" style="border-top:1px solid var(--fm-br,#E8E2DA);padding-top:12px;">
            <div class="d-flex justify-content-between py-1" style="font-size:13px;">
              <span :style="mutedStyle">Pickup</span>
              <span class="text-end" style="max-width:60%;">{{ booking.pickup_address }}</span>
            </div>
            <div class="d-flex justify-content-between py-1" style="font-size:13px;">
              <span :style="mutedStyle">Delivery</span>
              <span class="text-end" style="max-width:60%;">{{ booking.delivery_address }}</span>
            </div>
            <div class="d-flex justify-content-between py-1" style="font-size:13px;">
              <span :style="mutedStyle">Pickup date</span>
              <span>{{ booking.scheduled_pickup_date }}<span v-if="booking.scheduled_pickup_time"> · {{ booking.scheduled_pickup_time }}</span></span>
            </div>
            <div v-if="booking.transporter_name" class="d-flex justify-content-between py-1" style="font-size:13px;">
              <span :style="mutedStyle">Transporter</span>
              <span>{{ booking.transporter_name }}</span>
            </div>
          </div>

          <!-- Fully paid -->
          <div v-if="booking.payment_status === 'paid'" style="background:#E6F4E7;color:#2E7D32;padding:12px 14px;border-radius:8px;text-align:center;font-weight:600;">
            <i class="mdi mdi-check-circle me-1"></i>Fully paid — thank you!
          </div>

          <!-- Not yet paid in full -->
          <div v-else>
            <!-- Split-payment info banner -->
            <div v-if="needsSplit && !stkSent" style="background:#FFF8E7;border:1px solid #F2D58F;border-radius:8px;padding:10px 12px;margin-bottom:12px;font-size:12px;color:#7A5A0A;">
              <i class="mdi mdi-information-outline me-1"></i>
              M-Pesa limits single payments to KES {{ formatPrice(stkCeiling) }}. Your KES {{ formatPrice(outstanding) }} will come as <strong>{{ splitTotal - chunksDone }} prompts</strong> on your phone, one at a time. Tap below to send the first.
            </div>

            <!-- Pay button (first push or next chunk) -->
            <button v-if="!stkSent" class="btn btn-warning w-100 btn-lg fw-bold" :disabled="paying" @click="payNow">
              <span v-if="paying"><span class="spinner-border spinner-border-sm me-2"></span>Sending M-Pesa prompt…</span>
              <span v-else>
                <i class="mdi mdi-cellphone-arrow-down me-1"></i>
                {{ chunksDone === 0 ? 'Pay' : 'Pay next payment' }} — KES {{ formatPrice(nextChunkAmount) }}
                <span v-if="splitTotal > 1" style="font-weight:400;opacity:.85;font-size:13px;"> (payment {{ chunksDone + 1 }} of {{ splitTotal }})</span>
              </span>
            </button>

            <!-- Waiting for PIN -->
            <div v-else style="background:#FFF8E7;border:1px solid #F2D58F;border-radius:8px;padding:14px;text-align:center;">
              <i class="mdi mdi-cellphone-key" style="font-size:32px;color:#BFA164;"></i>
              <div class="mt-2 fw-bold">Check your phone</div>
              <div :style="mutedStyle" style="font-size:13px;">
                An M-Pesa prompt for <strong>KES {{ formatPrice(nextChunkAmount) }}</strong> has been sent to {{ maskedPhone }}.
                <span v-if="splitTotal > 1"> This is payment {{ currentChunkSeq }} of {{ splitTotal }}.</span>
                Enter your PIN to complete.
              </div>
              <div v-if="pollStatus" class="mt-2" :style="mutedStyle" style="font-size:12px;">
                <span class="spinner-border spinner-border-sm me-1" v-if="pollStatus === 'pending'"></span>
                {{ pollMessage }}
              </div>
              <button class="btn btn-sm btn-outline-secondary mt-3" @click="resendStk" :disabled="paying">
                <i class="mdi mdi-refresh me-1"></i>Resend prompt
              </button>
            </div>

            <!-- Success of last chunk, more to go -->
            <div v-if="lastChunkSuccess && booking.payment_status !== 'paid'" style="background:#E6F4E7;border:1px solid #2E7D32;border-radius:8px;padding:10px 12px;margin-top:12px;font-size:12px;color:#1a5e22;">
              <i class="mdi mdi-check me-1"></i>Payment {{ chunksDone }} received. Tap above to send the next prompt.
            </div>
          </div>
        </div>

        <p :style="mutedStyle" style="font-size:11px;text-align:center;">
          Paying to <strong>Flockr Paybill 4160303</strong> · Booking ref <strong>{{ booking.booking_ref }}</strong>
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* ===== MOBILE — feels-like-an-app ===== */
@media (max-width: 768px) {
  .tpp-container { padding-top: 60px !important; padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px)) !important; padding-left: 12px !important; padding-right: 12px !important; }
  .tpp-card { padding: 14px !important; border-radius: 10px !important; }
  .tpp-amount { font-size: 22px !important; }
  .tpp-input { font-size: 16px !important; padding: 10px 12px !important; }
  .tpp-btn { width: 100% !important; padding: 12px !important; font-size: 14px !important; }
}
</style>

<script>
/* eslint-disable */
import { executeJson, flocker } from "@/api";

export default {
  name: "TransportPaymentPage",
  data() {
    return {
      loading: true, error: null,
      booking: {}, totalDue: 0, paidSoFar: 0,
      paying: false, stkSent: false, lastChunkSuccess: false,
      stkRequestId: null, currentChunkSeq: 1,
      nextChunkAmount: 0, splitTotal: 1, chunksDone: 0,
      stkCeiling: 150000,
      pollStatus: null, pollMessage: '', pollTimer: null,
      darkMode: (document.documentElement.getAttribute('data-theme') || localStorage.getItem('flockr_theme') || 'dark') !== 'light',
    };
  },
  computed: {
    pageStyle() { return this.darkMode ? 'background:#0A0A0A;min-height:100vh;color:#F5F0E8;' : 'background:#F7F5F2;min-height:100vh;color:#1A1A1A;'; },
    cardStyle() { return this.darkMode ? 'background:#141414;border:1px solid #1E1E1E;border-radius:14px;' : 'background:#FFFFFF;border:1px solid #E8E2DA;border-radius:14px;'; },
    mutedStyle() { return this.darkMode ? 'color:#A09888;' : 'color:#666;'; },
    linkStyle() { return this.darkMode ? 'color:#BFA164;text-decoration:none;font-weight:600;' : 'color:#8B7355;text-decoration:none;font-weight:600;'; },
    outstanding() { return Math.max(0, this.totalDue - this.paidSoFar); },
    paidPercent() { return this.totalDue > 0 ? Math.min(100, Math.round((this.paidSoFar / this.totalDue) * 100)) : 0; },
    needsSplit() { return this.outstanding > this.stkCeiling; },
    maskedPhone() {
      var p = this.booking.requester_phone || '';
      if (!p) return 'your phone';
      return p.replace(/(\d{4})\d{3}(\d{3})/, '$1***$2');
    },
  },
  mounted() {
    this.fetchBooking();
  },
  beforeUnmount() { if (this.pollTimer) clearInterval(this.pollTimer); },
  methods: {
    formatPrice(v) { return Number(v || 0).toLocaleString(); },
    async fetchBooking() {
      try {
        var r = await executeJson({ function: 'getTransportBooking', bookingRef: this.$route.params.ref }, flocker);
        if (r.data && r.data.success && r.data.data && r.data.data.booking) {
          this.booking = r.data.data.booking;
          this.totalDue   = Number(this.booking.final_price || this.booking.quoted_price || 0);
          this.paidSoFar  = Number(this.booking.paid_amount || 0);
          if (!this.totalDue) this.error = 'No price has been set on this transport request yet — contact the admin.';
          // Estimate next chunk based on ceiling + outstanding (server is the source of truth, this is just for display before the user taps)
          this.nextChunkAmount = Math.min(this.stkCeiling, this.outstanding);
          this.splitTotal = Math.max(1, Math.ceil(this.outstanding / this.stkCeiling)) + this.chunksDone;
          this.chunksDone = Math.floor(this.paidSoFar / this.stkCeiling); // rough estimate
        } else {
          this.error = (r.data && r.data.message) || 'Booking not found';
        }
      } catch (e) { this.error = 'Network error — please try again'; }
      this.loading = false;
    },
    async payNow() {
      this.paying = true; this.lastChunkSuccess = false;
      try {
        var fn = this.chunksDone === 0 ? 'requestTransportPayment' : 'payNextSplit';
        var payload = fn === 'requestTransportPayment'
          ? { function: 'requestTransportPayment', bookingId: this.booking.id, requestedBy: sessionStorage.getItem('userId') || this.booking.requester_id || '' }
          : { function: 'payNextSplit', bookingId: this.booking.id };
        var r = await executeJson(payload, flocker);
        if (r.data && r.data.success) {
          var d = r.data.data || {};
          this.stkSent = true;
          this.stkRequestId    = d.stkRequestId;
          this.nextChunkAmount = d.amountThisChunk || this.nextChunkAmount;
          this.currentChunkSeq = d.splitSeq || (this.chunksDone + 1);
          this.splitTotal      = d.splitTotal || this.splitTotal;
          if (typeof d.totalDue === 'number')  this.totalDue  = d.totalDue;
          if (typeof d.paidSoFar === 'number') this.paidSoFar = d.paidSoFar;
          this.startPolling();
        } else {
          alert((r.data && r.data.message) || 'Could not initiate payment');
        }
      } catch (e) { alert('Network error — try again'); }
      this.paying = false;
    },
    async resendStk() {
      this.stkSent = false; this.pollStatus = null;
      if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null; }
      await this.payNow();
    },
    startPolling() {
      var self = this;
      var attempts = 0;
      self.pollStatus = 'pending'; self.pollMessage = 'Waiting for PIN…';
      if (self.pollTimer) clearInterval(self.pollTimer);
      self.pollTimer = setInterval(async function() {
        attempts++;
        if (attempts > 30) { clearInterval(self.pollTimer); self.pollStatus = 'timeout'; self.pollMessage = 'No response yet — try Resend if you didn\'t see the prompt.'; return; }
        try {
          var r = await executeJson({ function: 'checkPaymentStatus', requestId: self.stkRequestId }, flocker);
          var d = r.data && r.data.data;
          if (!d) return;
          self.pollStatus = d.status; self.pollMessage = d.message || '';
          if (d.status === 'success') {
            clearInterval(self.pollTimer); self.pollTimer = null;
            // Reload booking — paid_amount has incremented; if fully paid the UI shows the success state.
            await self.fetchBooking();
            self.stkSent = false;
            self.chunksDone = self.chunksDone + 1;
            self.lastChunkSuccess = true;
            self.nextChunkAmount = Math.min(self.stkCeiling, self.outstanding);
          } else if (['cancelled','failed','timeout'].indexOf(d.status) !== -1) {
            clearInterval(self.pollTimer); self.pollTimer = null;
            self.pollStatus = d.status;
          }
        } catch(e) {}
      }, 4000);
    },
  },
};
</script>
